<template>
  <section class="login" v-if="!$route.query.mobile">
    <div class="login-wrapper">
      <a href="https://familipay.com/" target="blank">
        <div class="fp-logo">
          <img src="@/assets/images/logo.png">
        </div>
      </a>
      <div class="login-form-wrapper box">
        <p class="small-text uppercase-text">{{ $lang.messages.welcomeBack }}</p>
        <h1 class="title uppercase-text">{{ $lang.messages.login }}</h1>
        <hr class="horizontal-line">
        <form
          class="login-form"
          name="loginForm"
          novalidate
          @submit.prevent
        >
          <!-- EMAIL -->
          <f-p-input-email
            :isRegister="false"
            :bluredProp="bluredProp"
            :emailProp="emailProp"
            @updateEmail="updateEmail"
            @clearOthersEmail="clearOthers">
          </f-p-input-email>
          
          <!-- PASSWORD -->
          <f-p-input-pass
            :bluredProp="bluredProp"
            :passwordProp="passwordProp"
            @updatePass="updatePass"
            @clearOthersPass="clearOthers"
            @isCapsOn="isCapsOn">
          </f-p-input-pass>
  
          <!-- CAPS LOCK HANDLER -->
          <f-p-is-caps-on
            :capsOnText="$lang.messages.capsIsOn"
            :isCapsOn="capsOn">
          </f-p-is-caps-on>
          
          <!-- SUBMIT & FORGET pass -->
          <f-p-input-button
            :buttonText="$lang.messages.login"
            :loading="loading"
            @formSubmit="onLoginFormSubmitted"
          >
            <div>
              <router-link class="login-link" :to="{ name: 'Reset' }">
                {{ $lang.messages.forgotPasswordQuestion }}
              </router-link>
            </div>
          </f-p-input-button>
        </form>
        
        <hr class="horizontal-line">
        
        <f-p-input-footer
          :regLabel="$lang.messages.ifYouDontHaveAccount"
          :leadsTo="'Register'"
          :regDesc="$lang.messages.registerHere">
        </f-p-input-footer>
        
      </div>
  
      <f-p-copyright
        :copLabel="$lang.messages.copyright"
        :copDesc="$lang.messages.familipay">
      </f-p-copyright>
      
    </div>

    <div class="footer">
      <img src="@/assets/images/login_illustration.png" class="login-illustration">
    </div>
  </section>
</template>

<script>
import { mapActions } from "vuex";
import { LOGIN, LOGOUT } from "@/store/action.types";
import STATUS_CODES from "@/common/statusCodes";
import FPInputEmail from "@/components/common/inputs/FPInputEmail";
import FPInputPass from "@/components/common/inputs/FPInputPass";
import FPInputFooter from "@/components/common/inputs/FPInputFooter";
import FPCopyright from "@/components/common/inputs/FPCopyright";
import FPInputButton from "@/components/common/inputs/FPInputButton";
import FPIsCapsOn from "@/components/common/inputs/FPIsCapsOn";

export default {
  name: "Login",
  components: {
    'f-p-input-email': FPInputEmail,
    'f-p-input-footer': FPInputFooter,
    'f-p-copyright': FPCopyright,
    'f-p-input-pass': FPInputPass,
    'f-p-input-button': FPInputButton,
    'f-p-is-caps-on': FPIsCapsOn
  },
  props: {
    bluredProp: {
      type: String,
      required: true
    },
    emailProp: {
      type: String,
      required: true
    },
    passwordProp: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    },
    capsOn: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formSubmitted: false
    }
  },
  created() {
    if (this.$route.query.mobile) {
      this.login(this.$route.query.email, this.$route.query.password);
    }
  },
  mounted() {
    if (this.$route.query.token_status === 'DEACTIVATION') {
      this.deactivationText();
    }
    window.addEventListener('storage', (e) => {
      if (e.key === 'user') {
        if (e.newValue !== '') {
          location.reload();
        }
      }
    });
  },
  destroyed() {
    this.$snotify.clear()
  },
  methods: {
    ...mapActions({
      loginAction: LOGIN,
      logoutAction: LOGOUT,
    }),
    onLoginFormSubmitted() {
      this.$validator.validateAll().then(result => {
        // On form submit remove the error toolip, but leave the error on input field
        this.$emit('clearInputs');
        
        // Don't leak the email and/or password when email or password is correct
        if (this.$validator.errors.items.length === 1) {
          this.$validator.errors.clear();
          this.statusError();
        }
        
        this.formSubmitted = true
        this.setMarginTooltip()
        if (result) {
          // On form submit show the loading spinner
          this.$emit('isLoading', true);
          
          this.login(this.emailProp, this.passwordProp);
        }
      });
    },
    login(email, password) {
      this.loginAction({
        email: email,
        password: password
      })
      .then(response => {
        if (response.data.data.countryCode === "US") {
          this.$lang.setLang("us")
        } else if (response.data.data.countryCode === "GB") {
          this.$lang.setLang("uk")
        }
    
        // On form submit after response remove the loading spinner
        this.$emit('isLoading', false);
        if (this.$route.query.payment) {
          this.$router.push({ name: "Preferences", query: {payment: 'true'} })
        } else {
          this.$router.push({name: "Preferences"})
        }
      })
      .catch(error => {
        // On form submit after any error in response remove the loading spinner
        this.$emit('isLoading', false);
    
        if (error.data.status.code === STATUS_CODES.FP_400 || error.data.status.code === STATUS_CODES.FP_401) {
          this.statusError()
          this.$snotify.error(this.$lang.messages.sn_invalidCredentials, "ERROR")
        } else if (error.data.status.code === STATUS_CODES.FP_403) {
          this.$snotify.error(this.$lang.messages.sn_loginEmailConfirm, "ERROR")
        }
      });
    },
    // This feature is currently disabled
    // It shows 'Thank you' message when user confirms his/her registration
    confirmedText() {
      this.$snotify.success(
        this.$lang.messages.sn_thanksConfirm,
        "SUCCESS", {
          timeout: 0
        }
      );
    },
    deactivationText() {
      this.$snotify.success(
        this.$lang.messages.sn_deactivation,
        "SUCCESS", {
          timeout: 0
        }
      );
    },
    statusError() {
      this.$validator.errors.add({
        field: "email",
        msg: this.$lang.messages.sn_incorrectEmailPass
      });
      this.$validator.errors.add({
        field: "password",
        msg: this.$lang.messages.sn_incorrectEmailPass
      });
    },
    setMarginTooltip() {
      let errorTooltip = document.getElementsByClassName("error-tooltip");

      for (let item of errorTooltip) {
        let height = item.offsetHeight;
        if (height >= 60) {
          item.style.marginTop = "-40%";
        } else {
          item.style.marginTop = "-85px";
        }
      }
    },
    // Listen to values inside FPInputEmail, call a function
    // and update the prop parent (LandingLayout) is sending to FPInputEmail
    updateEmail (val) {
      this.$emit('updateEmail', val)
    },
    // Listen to values inside FPInputPass, call a function
    // and update the prop parent (LandingLayout) is sending to FPInputPass
    updatePass (val) {
      this.$emit('updatePass', val)
    },
    // Clear input errors from children, and send value to LandingLayout
    clearOthers (val) {
      this.$emit('clearOthers', val)
    },
    // Turn caps on/off from child
    isCapsOn (val) {
      this.$emit('isCapsOn', val)
    }
  }
};
</script>
